import type { FormEvent } from "react";
import { useState } from "react";
import {
  gradesByParticipantType,
  type LeagueParticipantType,
} from "../data/leagueGrades";

export type { LeagueParticipantType } from "../data/leagueGrades";

type SubmissionState = {
  kind: "idle" | "submitting" | "error";
  message?: string;
};

type RegistrationPayload = {
  participant_type: LeagueParticipantType;
  grade: string;
  email: string;
  kaggle_id: string;
  website?: string;
};

type IssueResponse = {
  status?: string;
  message?: string;
  verification_id?: number;
  kaggle_id?: string;
  verification_text?: string;
  expires_at?: string;
};

const handleKaggleIdInput = (event: FormEvent<HTMLInputElement>) => {
  event.currentTarget.value = event.currentTarget.value.toLowerCase();
};

const registrationConfirmationFields: Array<{
  key: keyof RegistrationPayload;
  label: string;
}> = [
  { key: "grade", label: "学年" },
  { key: "email", label: "メールアドレス" },
  { key: "kaggle_id", label: "Kaggle ID" },
];

export function LeagueRegistrationForm({
  participantType = "regular",
}: {
  participantType?: LeagueParticipantType;
}) {
  const [payload, setPayload] = useState<RegistrationPayload | null>(null);
  const [isConfirmation, setIsConfirmation] = useState(false);
  const [submission, setSubmission] = useState<SubmissionState>({
    kind: "idle",
  });
  const isGraduate = participantType === "graduate";
  const isProfessional = participantType === "professional";
  const gradeOptions = gradesByParticipantType[participantType];
  const gradeLabel =
    gradeOptions.find((option) => option.value === payload?.grade)?.label ??
    payload?.grade;

  const handleInputSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const nextPayload = {
      ...Object.fromEntries(new FormData(form).entries()),
      participant_type: participantType,
    } as RegistrationPayload;

    setPayload(nextPayload);
    setSubmission({ kind: "idle" });
    setIsConfirmation(true);
  };

  const handleIssue = async () => {
    if (!payload) return;

    setSubmission({ kind: "submitting", message: "認証キーを発行しています…" });

    try {
      const response = await fetch("/api/league_issue.php", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = (await response.json().catch(() => ({}))) as IssueResponse;

      if (
        !response.ok ||
        result.status !== "ok" ||
        !result.verification_id ||
        !result.verification_text ||
        !result.kaggle_id
      ) {
        throw new Error(result.message || "認証キーを発行できませんでした。");
      }

      sessionStorage.setItem(
        "aiyss-league-verification",
        JSON.stringify({
          verification_id: result.verification_id,
          verification_text: result.verification_text,
          kaggle_id: result.kaggle_id,
          participant_type: participantType,
          expires_at: result.expires_at,
        })
      );
      window.location.assign("/league/register/verify/");
    } catch (error) {
      setSubmission({
        kind: "error",
        message:
          error instanceof Error
            ? error.message
            : "認証キーを発行できませんでした。時間をおいてお試しください。",
      });
    }
  };

  if (isConfirmation && payload) {
    return (
      <section
        className="league-register-form register-confirmation"
        aria-labelledby="register-confirm-title"
      >
        <div className="register-step-label">
          STEP 01 /{" "}
          {isGraduate
            ? "GRADUATE CONFIRMATION"
            : isProfessional
            ? "PROFESSIONAL CONFIRMATION"
            : participantType === "university"
            ? "UNIVERSITY CONFIRMATION"
            : "CONFIRMATION"}
        </div>
        <h3 id="register-confirm-title">入力内容を確認してください。</h3>
        <p className="register-confirmation-lead">
          内容に誤りがなければ、認証キーを発行します。発行後はKaggleのBio確認が必要です。
        </p>
        <dl className="register-summary-list">
          {registrationConfirmationFields.map(({ key, label }) => (
            <div key={key}>
              <dt>{key === "grade" && isProfessional ? "区分" : label}</dt>
              <dd>
                {key === "grade" ? gradeLabel || "—" : payload[key] || "—"}
              </dd>
            </div>
          ))}
        </dl>
        <div className="register-confirm-actions">
          <button
            className="league-secondary-button"
            type="button"
            onClick={() => {
              setIsConfirmation(false);
              setSubmission({ kind: "idle" });
            }}
            disabled={submission.kind === "submitting"}
          >
            入力内容を修正する
          </button>
          <button
            className="league-primary-link register-button"
            type="button"
            onClick={handleIssue}
            disabled={submission.kind === "submitting"}
          >
            {submission.kind === "submitting"
              ? "発行中…"
              : "認証キーを発行する"}
            <span aria-hidden="true">→</span>
          </button>
        </div>
        {submission.message && (
          <p className="register-status is-error" role="alert">
            {submission.message}
          </p>
        )}
      </section>
    );
  }

  return (
    <form className="league-register-form" onSubmit={handleInputSubmit}>
      <div className="register-step-label">
        STEP 01 /{" "}
        {isGraduate
          ? "GRADUATE INPUT"
          : isProfessional
          ? "PROFESSIONAL INPUT"
          : participantType === "university"
          ? "UNIVERSITY INPUT"
          : "INPUT"}
      </div>
      <div className="register-form-grid">
        <label>
          <span>
            {isProfessional ? "区分" : "学年"} <em>必須</em>
          </span>
          <select name="grade" defaultValue={payload?.grade ?? ""} required>
            <option value="" disabled>
              選択してください
            </option>
            {gradeOptions.map((grade) => (
              <option key={grade.value} value={grade.value}>
                {grade.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>
            メールアドレス <em>必須</em>
          </span>
          <input
            name="email"
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            defaultValue={payload?.email ?? ""}
          />
        </label>
        <label className="register-field-wide">
          <span>
            Kaggle ID <em>必須</em>
          </span>
          <input
            name="kaggle_id"
            type="text"
            required
            maxLength={100}
            placeholder="KaggleのプロフィールID"
            onInput={handleKaggleIdInput}
            defaultValue={payload?.kaggle_id ?? ""}
          />
        </label>
        <input
          className="register-honeypot"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
        />
      </div>

      <div className="register-submit-row">
        <button className="league-primary-link register-button" type="submit">
          {isGraduate
            ? "大学院生として登録する"
            : isProfessional
            ? "社会人として登録する"
            : participantType === "university"
            ? "大学生部門に登録する"
            : "リーグに登録する"}
          <span aria-hidden="true">→</span>
        </button>
      </div>
      <p className="register-note">
        {isGraduate
          ? "大学院生としてエキシビション総合に表示されます。登録情報はAIYSSの運営・連絡に使用します。"
          : isProfessional
          ? "社会人としてエキシビション総合に表示されます。登録情報はAIYSSの運営・連絡に使用します。"
          : participantType === "university"
          ? "大学生部門に表示されます。登録情報はAIYSSの運営・連絡に使用します。"
          : "登録情報はAIYSS年間リーグの運営・連絡に使用します。"}
      </p>
    </form>
  );
}
